import { BoundaryEditLegend } from "./panels/details/BoundaryEditLegend";
import { BoundaryPointCard } from "./panels/details/BoundaryPointCard";
import { buildPermission } from "common/utils/auth";
import { Button, LinearProgress, Paper, Typography } from "@mui/material";
import { capitalize, titleCase } from "portal/utils/strings";
import { classes, SMALL_BUTTON } from "portal/theme/theme";
import { DeepPartial } from "portal/utils/objects";
import { DetailsPanel } from "./panels/details/DetailsPanel";
import { farmApi } from "common/state/portalApi/farm";
import { FarmEditSafetyWarning } from "./FarmEditSafetyWarning";
import { FarmMap } from "./FarmMap";
import { Farm as FarmProto, Zone } from "protos/farm/farm";
import { farmWithoutRing } from "portal/utils/boundaryEdit";
import { Point as GeoPoint } from "protos/geo/geo";
import { getFarmBounds, getFieldBounds } from "common/utils/farm";
import { Header } from "portal/components/header/Header";
import {
  LngLat,
  LngLatBounds,
  Map as MapboxMap,
  PaddingOptions,
} from "mapbox-gl";
import { Navigate, useParams } from "react-router-dom";
import { NewZoneMenu } from "./NewZoneMenu";
import { Page } from "portal/components/Page";
import { Path } from "portal/utils/routing";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import {
  Selection,
  setFarm,
  setFocus,
  setPointsById,
  setSelection,
  setZonesById,
} from "portal/state/farmExplorer";
import { SelectionPanel } from "./panels/selection/SelectionPanel";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { toLngLatBounds } from "portal/utils/geo";
import { useAuthorizationRequired } from "portal/components/auth/WithAuthorizationRequired";
import { useBoundaryEditor } from "./panels/details/useBoundaryEditor";
import { useCanAddFarmGeometry } from "portal/views/farms/permissions";
import { useDispatch } from "react-redux";
import { useFarmExplorer, useSelf } from "portal/state/store";
import { useFarmFieldExportDialog } from "./FarmFieldExportDialog";
import { useFarmFieldImportDialog } from "./FarmFieldImportDialog";
import { useFarmMapData } from "common/hooks/useFarmMapData";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useSelectionPath } from "./useSelectionPath";
import { useTranslation } from "react-i18next";
import { WithSkeleton } from "portal/components/WithSkeleton";
import CleanUpIcon from "@mui/icons-material/CleaningServices";
import ExportFieldIcon from "@mui/icons-material/FileDownload";
import ImportFieldIcon from "@mui/icons-material/FileUpload";
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const FIT_PADDING = 100;

// a zone's area, or for a center pivot the circle its arm sweeps
const getZoneBounds = (
  zone: Zone,
  pointsById: Record<string, GeoPoint>
): LngLatBounds | undefined => {
  const areaBounds = toLngLatBounds(getFieldBounds(zone, pointsById));
  if (areaBounds) {
    return LngLatBounds.convert(areaBounds);
  }
  const pivot = zone.contents?.centerPivot;
  const center = pivot?.center?.id?.id
    ? pointsById[pivot.center.id.id]
    : undefined;
  if (!pivot || !center) {
    return;
  }
  const reach = pivot.legs.reduce((sum, leg) => sum + leg.lengthMeters, 0);
  return new LngLat(center.lng, center.lat).toBounds(reach);
};

// the details panel overlays the right of the map
const fitPadding = (panelWidth: number): PaddingOptions => ({
  top: FIT_PADDING,
  bottom: FIT_PADDING,
  left: FIT_PADDING,
  right: FIT_PADDING + panelWidth,
});

// every corner on screen
const isInView = (map: MapboxMap, bounds: LngLatBounds): boolean => {
  const { clientWidth, clientHeight } = map.getContainer();
  return [
    bounds.getNorthWest(),
    bounds.getNorthEast(),
    bounds.getSouthEast(),
    bounds.getSouthWest(),
  ].every((corner) => {
    const { x, y } = map.project(corner);
    return x >= 0 && x <= clientWidth && y >= 0 && y <= clientHeight;
  });
};

const fitZone = (
  map: MapboxMap,
  bounds: LngLatBounds,
  padding: PaddingOptions
): void => {
  map.fitBounds(bounds, {
    padding,
    // so a small obstacle keeps its surroundings
    maxZoom: 18,
    duration: 600,
    linear: true,
  });
};

export const Farm: FunctionComponent = () => {
  const { t } = useTranslation();
  const { farmId } = useParams();
  const dispatch = useDispatch();
  const {
    data: fetchedFarm,
    isSuccess,
    isLoading: farmLoading,
    refetch,
  } = useQueryPopups(farmApi.endpoints.getFarm.useQuery(farmId ?? skipToken));

  const { farm } = useFarmExplorer();

  useEffect(() => {
    dispatch(setFarm(fetchedFarm));
  }, [fetchedFarm, dispatch]);

  const [updateFarm, { isLoading: updateLoading }] = useMutationPopups(
    farmApi.endpoints.updateFarm.useMutation(),
    {
      success: titleCase(
        t("utils.actions.saved", {
          subject: t("views.farms.farm"),
        })
      ),
    }
  );

  const mutateFarm = async (
    newFarm: DeepPartial<FarmProto>
  ): Promise<boolean> => {
    const { data } = await updateFarm(newFarm);
    if (data) {
      // TODO: don't refetch, just set farm data to this.
      // this part of the farms api returns CaptureInfo enums incorrectly, so we can't use it just yet
      // setFarm(data);
      refetch();
      return true;
    }
    return false;
  };

  if (!farmId) {
    return <Navigate to={`/${Path.FARMS}`} />;
  }

  return (
    <>
      <Header
        title={capitalize(t("models.farms.farm_one"))}
        pageTitle={
          farm && `${farm.name} - ${capitalize(t("models.farms.farm_one"))}`
        }
        parentLink={Path.FARMS}
      />
      <Page maxWidth={false}>
        {/* like `<NoScroll>` but only for `md:` and above */}
        <div className="flex flex-col absolute inset-10 md:overflow-hidden">
          <WithSkeleton variant="rectangular" success={isSuccess}>
            {farm && (
              <FarmView
                farm={farm}
                updateFarm={mutateFarm}
                isLoading={farmLoading || updateLoading}
              />
            )}
          </WithSkeleton>
        </div>
      </Page>
    </>
  );
};

interface FarmViewProps {
  farm: FarmProto;
  updateFarm: (newFarm: DeepPartial<FarmProto>) => Promise<boolean>;
  isLoading: boolean;
}
const FarmView: FunctionComponent<FarmViewProps> = ({
  farm,
  isLoading,
  updateFarm,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showDetails, boundaryEdit, pointMove } = useFarmExplorer();
  // keyed on the ring, not the draft points: re-deriving FarmMapData is far
  // too slow for a drag
  const editedRing =
    boundaryEdit && !boundaryEdit.pending
      ? {
          zoneId: boundaryEdit.zoneId,
          areaIndex: boundaryEdit.areaIndex,
          holeIndex: boundaryEdit.holeIndex,
        }
      : undefined;
  const { zoneId, areaIndex, holeIndex } = editedRing ?? {};
  const effectiveFarm = useMemo(
    () =>
      zoneId === undefined || areaIndex === undefined
        ? farm
        : farmWithoutRing(farm, { zoneId, areaIndex, holeIndex }),
    [farm, zoneId, areaIndex, holeIndex]
  );
  const farmMapData = useFarmMapData(effectiveFarm);
  // a fresh array each render would defeat the memos in FarmLayers
  const farmsMapData = useMemo(() => [farmMapData], [farmMapData]);
  const self = useSelf();
  const internalToolsVisible =
    useAuthorizationRequired(
      buildPermission(
        PermissionAction.update,
        PermissionResource.farms,
        PermissionDomain.all
      )
    ) && self.isInternal;
  // the warning is for anyone who can change geometry at all; adding is the
  // wider of the two rights
  const canAddGeometry = useCanAddFarmGeometry();
  const boundaryPresent = farm.zones.some((zone) =>
    Boolean(zone.contents?.farmBoundary)
  );

  useEffect(() => {
    // cannot use dispatch inside memo so i need to listen for farmmapdata changes.
    dispatch(setPointsById(farmMapData.pointsById));
    dispatch(setZonesById(farmMapData.zonesById));
  }, [farmMapData, dispatch]);

  // from the stored farm, not the draft, so the camera never re-fits mid-edit
  const storedFarmBounds = useMemo(() => {
    const pointsById: Record<string, GeoPoint> = {};
    for (const { point } of farm.pointDefs) {
      if (point?.id) {
        pointsById[point.id.id] = point;
      }
    }
    return getFarmBounds(farm, pointsById);
  }, [farm]);

  const { pointsById, shownPointIds, selection, focus, pivotLegsDraft } =
    useFarmExplorer();
  const centerPivotLegsByZoneId = useMemo(
    () =>
      pivotLegsDraft
        ? { [pivotLegsDraft.zoneId]: pivotLegsDraft.legs }
        : undefined,
    [pivotLegsDraft]
  );

  const [map, setMap] = useState<MapboxMap>();
  const detailsPanelRef = useRef<HTMLDivElement>(null);
  const selectedZone =
    selection?.type === "zone" ? selection.selection : undefined;
  const selectedZoneId = selectedZone?.id?.id;
  const selectedZoneBounds = useMemo(
    () => selectedZone && getZoneBounds(selectedZone, farmMapData.pointsById),
    [selectedZone, farmMapData.pointsById]
  );
  // once per zone, so the refetch after a save doesn't move the camera again;
  // a click beside a field selects the farm boundary, which would zoom out
  const shownZoneIdRef = useRef<string>();
  const goToNextZoneRef = useRef(false);
  // a linked zone is shown even when it is already in view
  const goToNextZone = useCallback(() => {
    goToNextZoneRef.current = true;
  }, []);
  useSelectionPath(
    farm.id?.id,
    farmMapData.zonesById,
    farmMapData.pointsById,
    goToNextZone
  );
  useEffect(() => {
    if (!map || shownZoneIdRef.current === selectedZoneId) {
      return;
    }
    if (selectedZone && !selectedZoneBounds) {
      // a zone just saved has no points until the farm is refetched
      return;
    }
    shownZoneIdRef.current = selectedZoneId;
    const goTo = goToNextZoneRef.current;
    goToNextZoneRef.current = false;
    if (!selectedZoneBounds) {
      return;
    }
    if (
      !goTo &&
      (selectedZone?.contents?.farmBoundary ||
        isInView(map, selectedZoneBounds))
    ) {
      return;
    }
    fitZone(
      map,
      selectedZoneBounds,
      fitPadding(detailsPanelRef.current?.offsetWidth ?? 0)
    );
  }, [map, selectedZone, selectedZoneId, selectedZoneBounds]);
  const fitSelectedZone =
    map && selectedZoneBounds
      ? (): void =>
          fitZone(
            map,
            selectedZoneBounds,
            fitPadding(detailsPanelRef.current?.offsetWidth ?? 0)
          )
      : undefined;
  // selects a zone and fits the map to it, even when it is already in view
  const goToZone = (zone: Zone): void => {
    if (zone.id?.id === selectedZoneId) {
      fitSelectedZone?.();
      return;
    }
    goToNextZoneRef.current = true;
    dispatch(setSelection({ type: "zone", selection: zone }));
  };

  const editor = useBoundaryEditor();

  const { dialog: importFieldDialog, open: openImportFieldDialog } =
    useFarmFieldImportDialog(farm);
  const { dialog: exportDialog, open: openExportDialog } =
    useFarmFieldExportDialog(farm);

  const [garbageCollectFarm] = useMutationPopups(
    farmApi.endpoints.garbageCollectFarm.useMutation()
  );

  return (
    <div className="flex flex-col h-full">
      <Typography variant="h1" className="flex flex-row gap-2 text-4xl mb-3">
        {farm.name}
        {internalToolsVisible && (
          <>
            {importFieldDialog}
            {exportDialog}
            <Button
              startIcon={<ImportFieldIcon />}
              {...SMALL_BUTTON}
              onClick={openImportFieldDialog}
            >
              {t("views.farms.actions.importField")}
            </Button>
            <Button
              startIcon={<ExportFieldIcon />}
              {...SMALL_BUTTON}
              onClick={openExportDialog}
            >
              {t("views.farms.actions.exportField")}
            </Button>
            <Button
              startIcon={<CleanUpIcon />}
              {...SMALL_BUTTON}
              onClick={() => garbageCollectFarm(farm.id?.id ?? "")}
            >
              {t("views.farms.actions.cleanUpUnusedPoints")}
            </Button>
          </>
        )}
        {canAddGeometry && !boundaryEdit && !pointMove && (
          <div className="flex ml-auto">
            <NewZoneMenu boundaryPresent={boundaryPresent} />
          </div>
        )}
      </Typography>
      {canAddGeometry && <FarmEditSafetyWarning className="mb-3" />}
      {isLoading && <LinearProgress variant="indeterminate" />}
      <div className="flex flex-col md:flex-row flex-1 min-h-0">
        <div className="flex flex-col w-1/6">
          <SelectionPanel farm={farm} onGoToZone={goToZone} />
          <Paper className="flex flex-col grow" />
        </div>
        {/* extra wrapper to prevent map from having a weird effective min-height */}
        <div className="farm-map-container relative flex min-h-64 md:min-h-0 flex-1 overflow-hidden">
          <FarmMap
            className="absolute inset-0"
            selectedPointId={
              editor.active || selection?.type !== "point"
                ? undefined
                : selection.selection.id?.id
            }
            selectedZoneIds={
              selection?.type === "zone" && selection.selection.id?.id
                ? [selection.selection.id.id]
                : []
            }
            focusedPointId={
              focus?.type === "point" ? focus.selection.id?.id : undefined
            }
            focusedZoneIds={
              focus?.type === "zone" && focus.selection.id?.id
                ? [focus.selection.id.id]
                : []
            }
            shownPoints={editor.visibleStoredPoints(shownPointIds)}
            farmsMapData={farmsMapData}
            centerPivotLegsByZoneId={centerPivotLegsByZoneId}
            onPointClick={(f) => {
              if (editor.editingRing) {
                // selecting would leave the editor
                return;
              }
              const id = f?.properties.pointId;
              if (id) {
                const point = pointsById?.[id];
                dispatch(
                  setSelection(
                    point ? { type: "point", selection: point } : undefined
                  )
                );
              }
            }}
            onPointHover={(f) => {
              const id = f?.properties.pointId;
              if (id) {
                if (focus?.type === "point" && focus.selection.id?.id === id) {
                  return;
                }
                const point = pointsById?.[id];
                const newFocus: Selection | undefined = point
                  ? { type: "point", selection: point }
                  : undefined;
                dispatch(setFocus(newFocus));
              }
            }}
            onZoneClick={
              editor.editingRing
                ? undefined
                : (f) => {
                    const id = f?.properties.zoneId;
                    if (id) {
                      const zone = farm.zones.find((z) => z.id?.id === id);
                      dispatch(
                        setSelection(
                          zone ? { type: "zone", selection: zone } : undefined
                        )
                      );
                    }
                  }
            }
            onZoneHover={
              editor.editingRing
                ? undefined
                : (f) => {
                    const id = f?.properties.zoneId;
                    if (id) {
                      if (
                        focus?.type === "zone" &&
                        focus.selection.id?.id === id
                      ) {
                        return;
                      }
                      const zone = farm.zones.find((z) => z.id?.id === id);
                      dispatch(
                        setFocus(
                          zone ? { type: "zone", selection: zone } : undefined
                        )
                      );
                    } else {
                      dispatch(setFocus());
                    }
                  }
            }
            onMapReady={setMap}
            extension={editor}
            bounds={storedFarmBounds}
          />
          {boundaryEdit?.activePointId && (
            <div
              className={classes(
                "absolute top-0 z-10",
                showDetails ? "right-1/4" : "right-0"
              )}
            >
              <BoundaryPointCard />
            </div>
          )}
          {editor.active && (
            <div
              className={classes(
                "absolute bottom-0 left-0 z-10 max-w-lg pointer-events-none",
                showDetails ? "right-1/4" : "right-0"
              )}
            >
              <BoundaryEditLegend />
            </div>
          )}
          {showDetails && (
            // absolute so expanding a section can't resize the map and re-fit it
            <div
              ref={detailsPanelRef}
              className="absolute inset-y-0 right-0 z-10 flex w-1/4"
            >
              <DetailsPanel
                updateFarm={updateFarm}
                onFitZone={fitSelectedZone}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
